import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import * as yup from 'yup';

import {
  Box, Paper,
} from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate, useSearchParams } from 'react-router-dom';
import translator from '../../theme/translator.json';
import { useAppDispatch } from '../../store/Hooks';
import Spinner from '../../components/Spinner';
import Utils from '../../utils/Utils';
import AppBackground from '../../components/AppBackground';
import logoFull from '../../theme/assets/logo-full.png';
import MunicipalityService from '../../services/MunicipalityService';
import AlertUtil from '../../utils/AlertUtil';

const PasswordLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fromParam = searchParams.get('from');
    switch (fromParam) {
      case 'emailSent':
        AlertUtil.createSuccessAlert(Utils.getTranslation(translator.successMessages.emailSent), dispatch);
        break;
      default:
        break;
    }
  }, []);

  const login = async (values: any) => {
    try {
      setLoading(true);

      const { password } = values;

      const municipality = await MunicipalityService.getByPassword(password);

      await MunicipalityService.login(municipality.email, password);
      navigate('/');

      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      const message = await Utils.getTranslation(translator.errorMessages.municipalityLogin.login);
      if (message) AlertUtil.createErrorAlert(message as string, dispatch);
    }
  };

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          marginLeft: 'auto',
          marginRight: 'auto',
          position: 'relative',
        }}
      >
        <Formik
          initialValues={{
            password: '',
          }}
          validationSchema={yup.object({
            password: yup.string().required(Utils.getTranslation(translator.formMessages.requiredField)),
          })}
          onSubmit={(values, { setSubmitting }) => {
            login(values);
            setSubmitting(false);
          }}
        >
          {(formikProps) => (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '600px',
              width: '100%',
            }}>
              <Box sx={{
                maxWidth: '300px',
              }}>
                <img src={logoFull}/>
              </Box>
              <Paper sx={{
                margin: '15px',
                width: '100%',
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    width: '100%',
                  }}
                >
                  <Typography align="center" variant="h3">
                    {Utils.getTranslation(translator.pages.authMunicipality.title)}
                  </Typography>
                  <Typography align="center" variant="body2">
                    {Utils.getTranslation(translator.pages.authMunicipality.abstract)}
                  </Typography>
                  <Form style={{ width: '100%' }}>
                    <Field
                      component={TextField}
                      name="password"
                      type="password"
                      label={Utils.getTranslation(translator.pages.authMunicipality.code)}
                      margin='normal'
                      fullWidth
                    />
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                      >
                        {Utils.getTranslation(translator.pages.authMunicipality.submit)}
                      </Button>
                    </Box>
                  </Form>
                </Box>
              </Paper>
              <Link color="secondary" href="/login" variant="body2">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {Utils.getTranslation(translator.pages.authMunicipality.toAdminLogin)}
                  <KeyboardBackspaceIcon sx={{
                    transform: 'rotate(180deg)',
                  }} />
                </Box>
              </Link>
            </Box>
          )}
        </Formik>
        <Spinner show={loading}/>
      </Box>
      <AppBackground bgPos="bottom"/>
    </Container>
  );
};

export default PasswordLogin;
